import { ScoreDirection } from '@components-clean/central-monitoring/panels/panel-constants';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import { statusChips } from '@components-clean/core/grid/custom-cell/chip-constants';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { components } from 'types/rest-schema';

export const DummyPageData = {
    screens: {
        badges: 'Badges',
        charts: 'Charts',
        controls: 'Controls',
        dialogs: 'Dialogs',
        filters: 'Filters',
        grids: 'Grids',
        apiGid: 'API Grid',
        htmlElements: 'HTML Elements',
        inputs: 'Inputs',
        layout: 'Layout',
        splitter: 'Splitter',
        widget: 'Widget',
        dateInputs: 'Date Inputs',
        metadata: 'Metadata',
        ddList: 'Drag Drop List',
        infoField: 'Info field',
        icons: 'Icons',
        DVConfigPoC: 'Data View Config PoC',
        fonts: 'Fonts',
        bulkUpdate: 'Bulk Update',
        PersistentGridPoC: 'Persistent Grid PoC',
        HighlightRowGridPoC: 'Highlight Row Grid PoC',
        exceptions: 'Error Page',
        progressBars: 'Progress Bars',
        riskCommentsPoc: 'Risk Comments PoC',
        fileUpload: 'File Upload',
    },
};

export const GridCheckboxSelectionDummyColumns: GridColumnProps[] = [
    {
        id: 'checkbox',
        field: 'checkbox',
        columnType: 'checkbox',
        className: 'checkboxSelection',
        width: 40,
        customColumn: true,
        resizable: false,
        locked: true,
    },
    {
        id: 'ProductID',
        field: 'ProductID',
        title: 'ID',
        customColumn: true,
        width: 60,
        locked: true,
    },
    {
        id: 'general_info',
        title: 'General Info',
        locked: false,
        children: [
            {
                id: 'product_name',
                field: 'ProductName',
                title: 'Name',
                locked: false,
                customColumn: true,
                width: 350,
                maxLength: 20,
            },
            {
                id: 'category_name',
                field: 'Category.CategoryName',
                title: 'CategoryName',
                customColumn: true,
                width: 350,
                locked: false,
            },
        ],
    },
    {
        id: 'unit_price',
        field: 'UnitPrice',
        title: 'Price',
        customColumn: true,
        width: 50,
        locked: false,
    },
    {
        id: 'units_in_stock',
        field: 'UnitsInStock',
        title: 'In stock',
        width: 550,
        locked: false,
    },
];

const { ChipCell } = useGridCustomCell();

export const GridDummyColumns = [
    {
        id: 'id',
        field: 'ProductID',
        title: 'ID',
        width: 45,
        locked: true,
    },
    {
        id: 'general_info',
        title: 'General Info',
        locked: false,
        children: [
            {
                id: 'product_name',
                field: 'ProductName',
                title: 'Name',
                locked: false,
                width: 250,
                maxLength: 20,
            },
            {
                id: 'category_name',
                field: 'Category.CategoryName',
                title: 'CategoryName',
                width: 250,
                locked: false,
            },
        ],
    },
    {
        id: 'unit_price',
        field: 'UnitPrice',
        title: 'Price',
        width: 350,
        locked: false,
    },
    {
        id: 'units_in_stock',
        field: 'UnitsInStock',
        title: 'In stock',
        width: 550,
        locked: false,
    },
    {
        id: 'status',
        field: 'Status',
        title: 'Status',
        locked: false,
        width: 100,

        cell: ChipCell(statusChips, { width: 95 }),
    },
];

export interface NonRAGDataScore {
    valueInFocus: number;
    comparisonValue: number;
    changeDirection: ScoreDirection;
    colourInFocus?: number;
    comparisonColour?: number;
    colour?: number;
}

export interface NonRAGDataItem {
    Score: NonRAGDataScore;
    Change: number;
}

interface SparkLineData {
    x: string;
    y: number;
    color: 0 | 1 | 2 | 3 | 4;
    score: number;
}

export const NonRAGGridDummyData: NonRAGDataItem[] = [
    {
        Score: {
            valueInFocus: 150.71,
            comparisonValue: 200.51,
            changeDirection: ScoreDirection.Lower,
            colour: 1,
        },
        Change: 49.8,
    },
    {
        Score: {
            valueInFocus: 450.46,
            comparisonValue: 350.31,
            changeDirection: ScoreDirection.Upper,
            colour: 2,
        },
        Change: 150.15,
    },
    {
        Score: {
            valueInFocus: 351.14,
            comparisonValue: 452.26,
            changeDirection: ScoreDirection.Lower,
            colour: 3,
        },
        Change: 101.12,
    },
    {
        Score: {
            valueInFocus: 555.25,
            comparisonValue: 656.51,
            changeDirection: ScoreDirection.Upper,
            colour: 2,
        },
        Change: 101.26,
    },
    {
        Score: {
            valueInFocus: 150.75,
            comparisonValue: 200.56,
            changeDirection: ScoreDirection.Lower,
            colour: 0,
        },
        Change: 49.81,
    },
    {
        Score: {
            valueInFocus: 450.55,
            comparisonValue: 350.35,
            changeDirection: ScoreDirection.Upper,
            colour: 2,
        },
        Change: 150.2,
    },
    {
        Score: {
            valueInFocus: 351.34,
            comparisonValue: 452.72,
            changeDirection: ScoreDirection.Lower,
            colour: 3,
        },
        Change: 101.38,
    },
    {
        Score: {
            valueInFocus: 1456.45,
            comparisonValue: 1176.34,
            changeDirection: ScoreDirection.Upper,
            colour: 4,
        },
        Change: 280.11,
    },
    {
        Score: {
            valueInFocus: 1357.15,
            comparisonValue: 1741.26,
            changeDirection: ScoreDirection.Lower,
            colour: 4,
        },
        Change: 384.11,
    },
    {
        Score: {
            valueInFocus: 333.38,
            comparisonValue: 342.52,
            changeDirection: ScoreDirection.Lower,
            colour: 0,
        },
        Change: 9.14,
    },
    {
        Score: {
            valueInFocus: 110.36,
            comparisonValue: 125.47,
            changeDirection: ScoreDirection.Lower,
            colour: 0,
        },
        Change: 15.11,
    },
    {
        Score: {
            valueInFocus: 240.72,
            comparisonValue: 234.56,
            changeDirection: ScoreDirection.Upper,
            colour: 2,
        },
        Change: 6.16,
    },
    {
        Score: {
            valueInFocus: 13.52,
            comparisonValue: 26.74,
            changeDirection: ScoreDirection.Lower,
            colour: 1,
        },
        Change: 13.22,
    },
];

export const GridDummyData = [
    {
        ProductID: 1,
        ProductName: 'Chai',
        SupplierID: 1,
        CategoryID: 1,
        QuantityPerUnit: '10 boxes x 20 bags',
        UnitPrice: 18.0,
        UnitsInStock: 39,
        UnitsOnOrder: 0,
        ReorderLevel: 10,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Queued',
    },
    {
        ProductID: 2,
        ProductName: 'Chang',
        SupplierID: 1,
        CategoryID: 1,
        QuantityPerUnit: '24 - 12 oz bottles',
        UnitPrice: 19.0,
        UnitsInStock: 17,
        UnitsOnOrder: 40,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Ready',
    },
    {
        ProductID: 3,
        ProductName: 'Aniseed Syrup',
        SupplierID: 1,
        CategoryID: 2,
        QuantityPerUnit: '12 - 550 ml bottles',
        UnitPrice: 10.0,
        UnitsInStock: 13,
        UnitsOnOrder: 70,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Failed',
    },
    {
        ProductID: 4,
        ProductName: "Chef Anton's Cajun Seasoning",
        SupplierID: 2,
        CategoryID: 2,
        QuantityPerUnit: '48 - 6 oz jars',
        UnitPrice: 22.0,
        UnitsInStock: 53,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Ready',
    },
    {
        ProductID: 5,
        ProductName: "Chef Anton's Gumbo Mix",
        SupplierID: 2,
        CategoryID: 2,
        QuantityPerUnit: '36 boxes',
        UnitPrice: 21.35,
        UnitsInStock: 0,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: true,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 6,
        ProductName: "Grandma's Boysenberry Spread",
        SupplierID: 3,
        CategoryID: 2,
        QuantityPerUnit: '12 - 8 oz jars',
        UnitPrice: 25.0,
        UnitsInStock: 120,
        UnitsOnOrder: 0,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Failed',
    },
    {
        ProductID: 7,
        ProductName: "Uncle Bob's Organic Dried Pears",
        SupplierID: 3,
        CategoryID: 7,
        QuantityPerUnit: '12 - 1 lb pkgs.',
        UnitPrice: 30.0,
        UnitsInStock: 15,
        UnitsOnOrder: 0,
        ReorderLevel: 10,
        Discontinued: false,
        Category: {
            CategoryID: 7,
            CategoryName: 'Produce',
            Description: 'Dried fruit and bean curd',
        },
        Status: 'Ready',
    },
    {
        ProductID: 8,
        ProductName: 'Northwoods Cranberry Sauce',
        SupplierID: 3,
        CategoryID: 2,
        QuantityPerUnit: '12 - 12 oz jars',
        UnitPrice: 40.0,
        UnitsInStock: 6,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Queued',
    },
    {
        ProductID: 9,
        ProductName: 'Mishi Kobe Niku',
        SupplierID: 4,
        CategoryID: 6,
        QuantityPerUnit: '18 - 500 g pkgs.',
        UnitPrice: 97.0,
        UnitsInStock: 29,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: true,
        Category: {
            CategoryID: 6,
            CategoryName: 'Meat/Poultry',
            Description: 'Prepared meats',
        },
        Status: 'Failed',
    },
    {
        ProductID: 10,
        ProductName: 'Ikura',
        SupplierID: 4,
        CategoryID: 8,
        QuantityPerUnit: '12 - 200 ml jars',
        UnitPrice: 31.0,
        UnitsInStock: 31,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 11,
        ProductName: 'Queso Cabrales',
        SupplierID: 5,
        CategoryID: 4,
        QuantityPerUnit: '1 kg pkg.',
        UnitPrice: 21.0,
        UnitsInStock: 22,
        UnitsOnOrder: 30,
        ReorderLevel: 30,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Failed',
    },
    {
        ProductID: 12,
        ProductName: 'Queso Manchego La Pastora',
        SupplierID: 5,
        CategoryID: 4,
        QuantityPerUnit: '10 - 500 g pkgs.',
        UnitPrice: 38.0,
        UnitsInStock: 86,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Queued',
    },
    {
        ProductID: 13,
        ProductName: 'Konbu',
        SupplierID: 6,
        CategoryID: 8,
        QuantityPerUnit: '2 kg box',
        UnitPrice: 6.0,
        UnitsInStock: 24,
        UnitsOnOrder: 0,
        ReorderLevel: 5,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 14,
        ProductName: 'Tofu',
        SupplierID: 6,
        CategoryID: 7,
        QuantityPerUnit: '40 - 100 g pkgs.',
        UnitPrice: 23.25,
        UnitsInStock: 35,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 7,
            CategoryName: 'Produce',
            Description: 'Dried fruit and bean curd',
        },
        Status: 'Queued',
    },
    {
        ProductID: 15,
        ProductName: 'Genen Shouyu',
        SupplierID: 6,
        CategoryID: 2,
        QuantityPerUnit: '24 - 250 ml bottles',
        UnitPrice: 15.5,
        UnitsInStock: 39,
        UnitsOnOrder: 0,
        ReorderLevel: 5,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Ready',
    },
    {
        ProductID: 16,
        ProductName: 'Pavlova',
        SupplierID: 7,
        CategoryID: 3,
        QuantityPerUnit: '32 - 500 g boxes',
        UnitPrice: 17.45,
        UnitsInStock: 29,
        UnitsOnOrder: 0,
        ReorderLevel: 10,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 17,
        ProductName: 'Alice Mutton',
        SupplierID: 7,
        CategoryID: 6,
        QuantityPerUnit: '20 - 1 kg tins',
        UnitPrice: 39.0,
        UnitsInStock: 0,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: true,
        Category: {
            CategoryID: 6,
            CategoryName: 'Meat/Poultry',
            Description: 'Prepared meats',
        },
        Status: 'Failed',
    },
    {
        ProductID: 18,
        ProductName: 'Carnarvon Tigers',
        SupplierID: 7,
        CategoryID: 8,
        QuantityPerUnit: '16 kg pkg.',
        UnitPrice: 62.5,
        UnitsInStock: 42,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Ready',
    },
    {
        ProductID: 19,
        ProductName: 'Teatime Chocolate Biscuits',
        SupplierID: 8,
        CategoryID: 3,
        QuantityPerUnit: '10 boxes x 12 pieces',
        UnitPrice: 9.2,
        UnitsInStock: 25,
        UnitsOnOrder: 0,
        ReorderLevel: 5,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 20,
        ProductName: "Sir Rodney's Marmalade",
        SupplierID: 8,
        CategoryID: 3,
        QuantityPerUnit: '30 gift boxes',
        UnitPrice: 81.0,
        UnitsInStock: 40,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Failed',
    },
    {
        ProductID: 21,
        ProductName: "Sir Rodney's Scones",
        SupplierID: 8,
        CategoryID: 3,
        QuantityPerUnit: '24 pkgs. x 4 pieces',
        UnitPrice: 10.0,
        UnitsInStock: 3,
        UnitsOnOrder: 40,
        ReorderLevel: 5,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Ready',
    },
    {
        ProductID: 22,
        ProductName: "Gustaf's Knäckebröd",
        SupplierID: 9,
        CategoryID: 5,
        QuantityPerUnit: '24 - 500 g pkgs.',
        UnitPrice: 21.0,
        UnitsInStock: 104,
        UnitsOnOrder: 0,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 5,
            CategoryName: 'Grains/Cereals',
            Description: 'Breads, crackers, pasta, and cereal',
        },
        Status: 'Ready',
    },
    {
        ProductID: 23,
        ProductName: 'Tunnbröd',
        SupplierID: 9,
        CategoryID: 5,
        QuantityPerUnit: '12 - 250 g pkgs.',
        UnitPrice: 9.0,
        UnitsInStock: 61,
        UnitsOnOrder: 0,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 5,
            CategoryName: 'Grains/Cereals',
            Description: 'Breads, crackers, pasta, and cereal',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 24,
        ProductName: 'Guaraná Fantástica',
        SupplierID: 10,
        CategoryID: 1,
        QuantityPerUnit: '12 - 355 ml cans',
        UnitPrice: 4.5,
        UnitsInStock: 20,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: true,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 25,
        ProductName: 'NuNuCa Nuß-Nougat-Creme',
        SupplierID: 11,
        CategoryID: 3,
        QuantityPerUnit: '20 - 450 g glasses',
        UnitPrice: 14.0,
        UnitsInStock: 76,
        UnitsOnOrder: 0,
        ReorderLevel: 30,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Queued',
    },
    {
        ProductID: 26,
        ProductName: 'Gumbär Gummibärchen',
        SupplierID: 11,
        CategoryID: 3,
        QuantityPerUnit: '100 - 250 g bags',
        UnitPrice: 31.23,
        UnitsInStock: 15,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Failed',
    },
    {
        ProductID: 27,
        ProductName: 'Schoggi Schokolade',
        SupplierID: 11,
        CategoryID: 3,
        QuantityPerUnit: '100 - 100 g pieces',
        UnitPrice: 43.9,
        UnitsInStock: 49,
        UnitsOnOrder: 0,
        ReorderLevel: 30,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Queued',
    },
    {
        ProductID: 28,
        ProductName: 'Rössle Sauerkraut',
        SupplierID: 12,
        CategoryID: 7,
        QuantityPerUnit: '25 - 825 g cans',
        UnitPrice: 45.6,
        UnitsInStock: 26,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: true,
        Category: {
            CategoryID: 7,
            CategoryName: 'Produce',
            Description: 'Dried fruit and bean curd',
        },
        Status: 'Ready',
    },
    {
        ProductID: 29,
        ProductName: 'Thüringer Rostbratwurst',
        SupplierID: 12,
        CategoryID: 6,
        QuantityPerUnit: '50 bags x 30 sausgs.',
        UnitPrice: 123.79,
        UnitsInStock: 0,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: true,
        Category: {
            CategoryID: 6,
            CategoryName: 'Meat/Poultry',
            Description: 'Prepared meats',
        },
        Status: 'Ready',
    },
    {
        ProductID: 30,
        ProductName: 'Nord-Ost Matjeshering',
        SupplierID: 13,
        CategoryID: 8,
        QuantityPerUnit: '10 - 200 g glasses',
        UnitPrice: 25.89,
        UnitsInStock: 10,
        UnitsOnOrder: 0,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Failed',
    },
    {
        ProductID: 31,
        ProductName: 'Gorgonzola Telino',
        SupplierID: 14,
        CategoryID: 4,
        QuantityPerUnit: '12 - 100 g pkgs',
        UnitPrice: 12.5,
        UnitsInStock: 0,
        UnitsOnOrder: 70,
        ReorderLevel: 20,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 32,
        ProductName: 'Mascarpone Fabioli',
        SupplierID: 14,
        CategoryID: 4,
        QuantityPerUnit: '24 - 200 g pkgs.',
        UnitPrice: 32.0,
        UnitsInStock: 9,
        UnitsOnOrder: 40,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Queued',
    },
    {
        ProductID: 33,
        ProductName: 'Geitost',
        SupplierID: 15,
        CategoryID: 4,
        QuantityPerUnit: '500 g',
        UnitPrice: 2.5,
        UnitsInStock: 112,
        UnitsOnOrder: 0,
        ReorderLevel: 20,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Ready',
    },
    {
        ProductID: 34,
        ProductName: 'Sasquatch Ale',
        SupplierID: 16,
        CategoryID: 1,
        QuantityPerUnit: '24 - 12 oz bottles',
        UnitPrice: 14.0,
        UnitsInStock: 111,
        UnitsOnOrder: 0,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Failed',
    },
    {
        ProductID: 35,
        ProductName: 'Steeleye Stout',
        SupplierID: 16,
        CategoryID: 1,
        QuantityPerUnit: '24 - 12 oz bottles',
        UnitPrice: 18.0,
        UnitsInStock: 20,
        UnitsOnOrder: 0,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Queued',
    },
    {
        ProductID: 36,
        ProductName: 'Inlagd Sill',
        SupplierID: 17,
        CategoryID: 8,
        QuantityPerUnit: '24 - 250 g  jars',
        UnitPrice: 19.0,
        UnitsInStock: 112,
        UnitsOnOrder: 0,
        ReorderLevel: 20,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Ready',
    },
    {
        ProductID: 37,
        ProductName: 'Gravad lax',
        SupplierID: 17,
        CategoryID: 8,
        QuantityPerUnit: '12 - 500 g pkgs.',
        UnitPrice: 26.0,
        UnitsInStock: 11,
        UnitsOnOrder: 50,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Failed',
    },
    {
        ProductID: 38,
        ProductName: 'Côte de Blaye',
        SupplierID: 18,
        CategoryID: 1,
        QuantityPerUnit: '12 - 75 cl bottles',
        UnitPrice: 263.5,
        UnitsInStock: 17,
        UnitsOnOrder: 0,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 39,
        ProductName: 'Chartreuse verte',
        SupplierID: 18,
        CategoryID: 1,
        QuantityPerUnit: '750 cc per bottle',
        UnitPrice: 18.0,
        UnitsInStock: 69,
        UnitsOnOrder: 0,
        ReorderLevel: 5,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 40,
        ProductName: 'Boston Crab Meat',
        SupplierID: 19,
        CategoryID: 8,
        QuantityPerUnit: '24 - 4 oz tins',
        UnitPrice: 18.4,
        UnitsInStock: 123,
        UnitsOnOrder: 0,
        ReorderLevel: 30,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Ready',
    },
    {
        ProductID: 41,
        ProductName: "Jack's New England Clam Chowder",
        SupplierID: 19,
        CategoryID: 8,
        QuantityPerUnit: '12 - 12 oz cans',
        UnitPrice: 9.65,
        UnitsInStock: 85,
        UnitsOnOrder: 0,
        ReorderLevel: 10,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 42,
        ProductName: 'Singaporean Hokkien Fried Mee',
        SupplierID: 20,
        CategoryID: 5,
        QuantityPerUnit: '32 - 1 kg pkgs.',
        UnitPrice: 14.0,
        UnitsInStock: 26,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: true,
        Category: {
            CategoryID: 5,
            CategoryName: 'Grains/Cereals',
            Description: 'Breads, crackers, pasta, and cereal',
        },
        Status: 'Failed',
    },
    {
        ProductID: 43,
        ProductName: 'Ipoh Coffee',
        SupplierID: 20,
        CategoryID: 1,
        QuantityPerUnit: '16 - 500 g tins',
        UnitPrice: 46.0,
        UnitsInStock: 17,
        UnitsOnOrder: 10,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Queued',
    },
    {
        ProductID: 44,
        ProductName: 'Gula Malacca',
        SupplierID: 20,
        CategoryID: 2,
        QuantityPerUnit: '20 - 2 kg bags',
        UnitPrice: 19.45,
        UnitsInStock: 27,
        UnitsOnOrder: 0,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Ready',
    },
    {
        ProductID: 45,
        ProductName: 'Rogede sild',
        SupplierID: 21,
        CategoryID: 8,
        QuantityPerUnit: '1k pkg.',
        UnitPrice: 9.5,
        UnitsInStock: 5,
        UnitsOnOrder: 70,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 46,
        ProductName: 'Spegesild',
        SupplierID: 21,
        CategoryID: 8,
        QuantityPerUnit: '4 - 450 g glasses',
        UnitPrice: 12.0,
        UnitsInStock: 95,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Ready',
    },
    {
        ProductID: 47,
        ProductName: 'Zaanse koeken',
        SupplierID: 22,
        CategoryID: 3,
        QuantityPerUnit: '10 - 4 oz boxes',
        UnitPrice: 9.5,
        UnitsInStock: 36,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 48,
        ProductName: 'Chocolade',
        SupplierID: 22,
        CategoryID: 3,
        QuantityPerUnit: '10 pkgs.',
        UnitPrice: 12.75,
        UnitsInStock: 15,
        UnitsOnOrder: 70,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 49,
        ProductName: 'Maxilaku',
        SupplierID: 23,
        CategoryID: 3,
        QuantityPerUnit: '24 - 50 g pkgs.',
        UnitPrice: 20.0,
        UnitsInStock: 10,
        UnitsOnOrder: 60,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Ready',
    },
    {
        ProductID: 50,
        ProductName: 'Valkoinen suklaa',
        SupplierID: 23,
        CategoryID: 3,
        QuantityPerUnit: '12 - 100 g bars',
        UnitPrice: 16.25,
        UnitsInStock: 65,
        UnitsOnOrder: 0,
        ReorderLevel: 30,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Ready',
    },
    {
        ProductID: 51,
        ProductName: 'Manjimup Dried Apples',
        SupplierID: 24,
        CategoryID: 7,
        QuantityPerUnit: '50 - 300 g pkgs.',
        UnitPrice: 53.0,
        UnitsInStock: 20,
        UnitsOnOrder: 0,
        ReorderLevel: 10,
        Discontinued: false,
        Category: {
            CategoryID: 7,
            CategoryName: 'Produce',
            Description: 'Dried fruit and bean curd',
        },
        Status: 'Ready',
    },
    {
        ProductID: 52,
        ProductName: 'Filo Mix',
        SupplierID: 24,
        CategoryID: 5,
        QuantityPerUnit: '16 - 2 kg boxes',
        UnitPrice: 7.0,
        UnitsInStock: 38,
        UnitsOnOrder: 0,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 5,
            CategoryName: 'Grains/Cereals',
            Description: 'Breads, crackers, pasta, and cereal',
        },
        Status: 'Failed',
    },
    {
        ProductID: 53,
        ProductName: 'Perth Pasties',
        SupplierID: 24,
        CategoryID: 6,
        QuantityPerUnit: '48 pieces',
        UnitPrice: 32.8,
        UnitsInStock: 0,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: true,
        Category: {
            CategoryID: 6,
            CategoryName: 'Meat/Poultry',
            Description: 'Prepared meats',
        },
        Status: 'Failed',
    },
    {
        ProductID: 54,
        ProductName: 'Tourtière',
        SupplierID: 25,
        CategoryID: 6,
        QuantityPerUnit: '16 pies',
        UnitPrice: 7.45,
        UnitsInStock: 21,
        UnitsOnOrder: 0,
        ReorderLevel: 10,
        Discontinued: false,
        Category: {
            CategoryID: 6,
            CategoryName: 'Meat/Poultry',
            Description: 'Prepared meats',
        },
        Status: 'Ready',
    },
    {
        ProductID: 55,
        ProductName: 'Pâté chinois',
        SupplierID: 25,
        CategoryID: 6,
        QuantityPerUnit: '24 boxes x 2 pies',
        UnitPrice: 24.0,
        UnitsInStock: 115,
        UnitsOnOrder: 0,
        ReorderLevel: 20,
        Discontinued: false,
        Category: {
            CategoryID: 6,
            CategoryName: 'Meat/Poultry',
            Description: 'Prepared meats',
        },
        Status: 'Ready',
    },
    {
        ProductID: 56,
        ProductName: 'Gnocchi di nonna Alice',
        SupplierID: 26,
        CategoryID: 5,
        QuantityPerUnit: '24 - 250 g pkgs.',
        UnitPrice: 38.0,
        UnitsInStock: 21,
        UnitsOnOrder: 10,
        ReorderLevel: 30,
        Discontinued: false,
        Category: {
            CategoryID: 5,
            CategoryName: 'Grains/Cereals',
            Description: 'Breads, crackers, pasta, and cereal',
        },
        Status: 'Queued',
    },
    {
        ProductID: 57,
        ProductName: 'Ravioli Angelo',
        SupplierID: 26,
        CategoryID: 5,
        QuantityPerUnit: '24 - 250 g pkgs.',
        UnitPrice: 19.5,
        UnitsInStock: 36,
        UnitsOnOrder: 0,
        ReorderLevel: 20,
        Discontinued: false,
        Category: {
            CategoryID: 5,
            CategoryName: 'Grains/Cereals',
            Description: 'Breads, crackers, pasta, and cereal',
        },
        Status: 'Queued',
    },
    {
        ProductID: 58,
        ProductName: 'Escargots de Bourgogne',
        SupplierID: 27,
        CategoryID: 8,
        QuantityPerUnit: '24 pieces',
        UnitPrice: 13.25,
        UnitsInStock: 62,
        UnitsOnOrder: 0,
        ReorderLevel: 20,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Failed',
    },
    {
        ProductID: 59,
        ProductName: 'Raclette Courdavault',
        SupplierID: 28,
        CategoryID: 4,
        QuantityPerUnit: '5 kg pkg.',
        UnitPrice: 55.0,
        UnitsInStock: 79,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Failed',
    },
    {
        ProductID: 60,
        ProductName: 'Camembert Pierrot',
        SupplierID: 28,
        CategoryID: 4,
        QuantityPerUnit: '15 - 300 g rounds',
        UnitPrice: 34.0,
        UnitsInStock: 19,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Queued',
    },
    {
        ProductID: 61,
        ProductName: "Sirop d'érable",
        SupplierID: 29,
        CategoryID: 2,
        QuantityPerUnit: '24 - 500 ml bottles',
        UnitPrice: 28.5,
        UnitsInStock: 113,
        UnitsOnOrder: 0,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Queued',
    },
    {
        ProductID: 62,
        ProductName: 'Tarte au sucre',
        SupplierID: 29,
        CategoryID: 3,
        QuantityPerUnit: '48 pies',
        UnitPrice: 49.3,
        UnitsInStock: 17,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 63,
        ProductName: 'Vegie-spread',
        SupplierID: 7,
        CategoryID: 2,
        QuantityPerUnit: '15 - 625 g jars',
        UnitPrice: 43.9,
        UnitsInStock: 24,
        UnitsOnOrder: 0,
        ReorderLevel: 5,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Failed',
    },
    {
        ProductID: 64,
        ProductName: 'Wimmers gute Semmelknödel',
        SupplierID: 12,
        CategoryID: 5,
        QuantityPerUnit: '20 bags x 4 pieces',
        UnitPrice: 33.25,
        UnitsInStock: 22,
        UnitsOnOrder: 80,
        ReorderLevel: 30,
        Discontinued: false,
        Category: {
            CategoryID: 5,
            CategoryName: 'Grains/Cereals',
            Description: 'Breads, crackers, pasta, and cereal',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 65,
        ProductName: 'Louisiana Fiery Hot Pepper Sauce',
        SupplierID: 2,
        CategoryID: 2,
        QuantityPerUnit: '32 - 8 oz bottles',
        UnitPrice: 21.05,
        UnitsInStock: 76,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 66,
        ProductName: 'Louisiana Hot Spiced Okra',
        SupplierID: 2,
        CategoryID: 2,
        QuantityPerUnit: '24 - 8 oz jars',
        UnitPrice: 17.0,
        UnitsInStock: 4,
        UnitsOnOrder: 100,
        ReorderLevel: 20,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Failed',
    },
    {
        ProductID: 67,
        ProductName: 'Laughing Lumberjack Lager',
        SupplierID: 16,
        CategoryID: 1,
        QuantityPerUnit: '24 - 12 oz bottles',
        UnitPrice: 14.0,
        UnitsInStock: 52,
        UnitsOnOrder: 0,
        ReorderLevel: 10,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Ready',
    },
    {
        ProductID: 68,
        ProductName: 'Scottish Longbreads',
        SupplierID: 8,
        CategoryID: 3,
        QuantityPerUnit: '10 boxes x 8 pieces',
        UnitPrice: 12.5,
        UnitsInStock: 6,
        UnitsOnOrder: 10,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 3,
            CategoryName: 'Confections',
            Description: 'Desserts, candies, and sweet breads',
        },
        Status: 'Ready',
    },
    {
        ProductID: 69,
        ProductName: 'Gudbrandsdalsost',
        SupplierID: 15,
        CategoryID: 4,
        QuantityPerUnit: '10 kg pkg.',
        UnitPrice: 36.0,
        UnitsInStock: 26,
        UnitsOnOrder: 0,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Queued',
    },
    {
        ProductID: 70,
        ProductName: 'Outback Lager',
        SupplierID: 7,
        CategoryID: 1,
        QuantityPerUnit: '24 - 355 ml bottles',
        UnitPrice: 15.0,
        UnitsInStock: 15,
        UnitsOnOrder: 10,
        ReorderLevel: 30,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Failed',
    },
    {
        ProductID: 71,
        ProductName: 'Flotemysost',
        SupplierID: 15,
        CategoryID: 4,
        QuantityPerUnit: '10 - 500 g pkgs.',
        UnitPrice: 21.5,
        UnitsInStock: 26,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Queued',
    },
    {
        ProductID: 72,
        ProductName: 'Mozzarella di Giovanni',
        SupplierID: 14,
        CategoryID: 4,
        QuantityPerUnit: '24 - 200 g pkgs.',
        UnitPrice: 34.8,
        UnitsInStock: 14,
        UnitsOnOrder: 0,
        ReorderLevel: 0,
        Discontinued: false,
        Category: {
            CategoryID: 4,
            CategoryName: 'Dairy Products',
            Description: 'Cheeses',
        },
        Status: 'Obsolete',
    },
    {
        ProductID: 73,
        ProductName: 'Röd Kaviar',
        SupplierID: 17,
        CategoryID: 8,
        QuantityPerUnit: '24 - 150 g jars',
        UnitPrice: 15.0,
        UnitsInStock: 101,
        UnitsOnOrder: 0,
        ReorderLevel: 5,
        Discontinued: false,
        Category: {
            CategoryID: 8,
            CategoryName: 'Seafood',
            Description: 'Seaweed and fish',
        },
        Status: 'Failed',
    },
    {
        ProductID: 74,
        ProductName: 'Longlife Tofu',
        SupplierID: 4,
        CategoryID: 7,
        QuantityPerUnit: '5 kg pkg.',
        UnitPrice: 10.0,
        UnitsInStock: 4,
        UnitsOnOrder: 20,
        ReorderLevel: 5,
        Discontinued: false,
        Category: {
            CategoryID: 7,
            CategoryName: 'Produce',
            Description: 'Dried fruit and bean curd',
        },
        Status: 'Queued',
    },
    {
        ProductID: 75,
        ProductName: 'Rhönbräu Klosterbier',
        SupplierID: 12,
        CategoryID: 1,
        QuantityPerUnit: '24 - 0.5 l bottles',
        UnitPrice: 7.75,
        UnitsInStock: 125,
        UnitsOnOrder: 0,
        ReorderLevel: 25,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Ready',
    },
    {
        ProductID: 76,
        ProductName: 'Lakkalikööri',
        SupplierID: 23,
        CategoryID: 1,
        QuantityPerUnit: '500 ml',
        UnitPrice: 18.0,
        UnitsInStock: 57,
        UnitsOnOrder: 0,
        ReorderLevel: 20,
        Discontinued: false,
        Category: {
            CategoryID: 1,
            CategoryName: 'Beverages',
            Description: 'Soft drinks, coffees, teas, beers, and ales',
        },
        Status: 'Failed',
    },
    {
        ProductID: 77,
        ProductName: 'Original Frankfurter grüne Soße',
        SupplierID: 12,
        CategoryID: 2,
        QuantityPerUnit: '12 boxes',
        UnitPrice: 13.0,
        UnitsInStock: 32,
        UnitsOnOrder: 0,
        ReorderLevel: 15,
        Discontinued: false,
        Category: {
            CategoryID: 2,
            CategoryName: 'Condiments',
            Description: 'Sweet and savory sauces, relishes, spreads, and seasonings',
        },
        Status: 'Obsolete',
    },
];

export const SubjectByVisitData: components['schemas']['LineResultDto'] = {
    threshold1: 35.5,
    threshold2: 50.55,
    lineSubjectForSubjects: null,
    lineSubjectByVisitForStudy: {
        '1': {
            visits: [
                {
                    x: 'Baseline',
                    value: 35.24,
                    subjectId: 1,
                    siteId: 2023,
                    index: 0,
                },
                {
                    x: 'Visit 1',
                    value: 53.49,
                    subjectId: 1,
                    siteId: 2023,
                    index: 1,
                },
                {
                    x: 'Visit 2',
                    value: null,
                    subjectId: 1,
                    siteId: 2023,
                    index: 2,
                },
                {
                    x: 'Visit 3',
                    value: 57.29,
                    subjectId: 1,
                    siteId: 2023,
                    index: 3,
                },
                {
                    x: 'Visit 4',
                    value: 57.84,
                    subjectId: 1,
                    siteId: 2023,
                    index: 4,
                },
                {
                    x: 'Visit 5',
                    value: null,
                    subjectId: 1,
                    siteId: 2023,
                    index: 5,
                },
                {
                    x: 'Visit 6',
                    value: null,
                    subjectId: 1,
                    siteId: 2023,
                    index: 6,
                },
                {
                    x: 'Visit 7',
                    value: 59.3,
                    subjectId: 1,
                    siteId: 2023,
                    index: 7,
                },
                {
                    x: 'Visit 8',
                    value: 33.08,
                    subjectId: 1,
                    siteId: 2023,
                    index: 8,
                },
                {
                    x: 'Visit 9',
                    value: 30.74,
                    subjectId: 1,
                    siteId: 2023,
                    index: 9,
                },
                {
                    x: 'Visit 10',
                    value: 60.27,
                    subjectId: 1,
                    siteId: 2023,
                    index: 10,
                },
                {
                    x: 'Visit 11',
                    value: 37.7,
                    subjectId: 1,
                    siteId: 2023,
                    index: 11,
                },
                {
                    x: 'Safety FUP',
                    value: null,
                    subjectId: 1,
                    siteId: 2023,
                    index: 12,
                },
            ],
            subjectNumber: '101-232-001',
        },
        '2': {
            visits: [
                {
                    x: 'Baseline',
                    value: 58.34,
                    subjectId: 2,
                    siteId: 2023,
                    index: 0,
                },
                {
                    x: 'Visit 1',
                    value: 56.29,
                    subjectId: 2,
                    siteId: 2023,
                    index: 1,
                },
                {
                    x: 'Visit 2',
                    value: 56.06,
                    subjectId: 2,
                    siteId: 2023,
                    index: 2,
                },
                {
                    x: 'Visit 3',
                    value: 30.48,
                    subjectId: 2,
                    siteId: 2023,
                    index: 3,
                },
                {
                    x: 'Visit 4',
                    value: 46.23,
                    subjectId: 2,
                    siteId: 2023,
                    index: 4,
                },
                {
                    x: 'Visit 5',
                    value: 33.15,
                    subjectId: 2,
                    siteId: 2023,
                    index: 5,
                },
            ],
            subjectNumber: '101-232-002',
        },
        '3': {
            visits: [
                {
                    x: 'Baseline',
                    value: 42.33,
                    subjectId: 3,
                    siteId: 2023,
                    index: 0,
                },
                {
                    x: 'Visit 1',
                    value: 38.82,
                    subjectId: 3,
                    siteId: 2023,
                    index: 1,
                },
                {
                    x: 'Visit 2',
                    value: 35.5,
                    subjectId: 3,
                    siteId: 2023,
                    index: 2,
                },
                {
                    x: 'Visit 3',
                    value: 41.07,
                    subjectId: 3,
                    siteId: 2023,
                    index: 3,
                },
                {
                    x: 'Visit 4',
                    value: 58.63,
                    subjectId: 3,
                    siteId: 2023,
                    index: 4,
                },
                {
                    x: 'Visit 5',
                    value: 41.02,
                    subjectId: 3,
                    siteId: 2023,
                    index: 5,
                },
                {
                    x: 'Visit 6',
                    value: 38.79,
                    subjectId: 3,
                    siteId: 2023,
                    index: 6,
                },
                {
                    x: 'Visit 7',
                    value: 32.53,
                    subjectId: 3,
                    siteId: 2023,
                    index: 7,
                },
                {
                    x: 'Visit 8',
                    value: 51.86,
                    subjectId: 3,
                    siteId: 2023,
                    index: 8,
                },
                {
                    x: 'Visit 9',
                    value: 40.99,
                    subjectId: 3,
                    siteId: 2023,
                    index: 9,
                },
            ],
            subjectNumber: '101-232-003',
        },
        '4': {
            visits: [
                {
                    x: 'Baseline',
                    value: 59.26,
                    subjectId: 4,
                    siteId: 2024,
                    index: 0,
                },
                {
                    x: 'Visit 1',
                    value: 38.23,
                    subjectId: 4,
                    siteId: 2024,
                    index: 1,
                },
                {
                    x: 'Visit 2',
                    value: 46.28,
                    subjectId: 4,
                    siteId: 2024,
                    index: 2,
                },
                {
                    x: 'Visit 3',
                    value: 43.69,
                    subjectId: 4,
                    siteId: 2024,
                    index: 3,
                },
            ],
            subjectNumber: '101-232-004',
        },
        '5': {
            visits: [
                {
                    x: 'Baseline',
                    value: 46.24,
                    subjectId: 5,
                    siteId: 2024,
                    index: 0,
                },
                {
                    x: 'Visit 1',
                    value: 43.2,
                    subjectId: 5,
                    siteId: 2024,
                    index: 1,
                },
                {
                    x: 'Visit 2',
                    value: 48.37,
                    subjectId: 5,
                    siteId: 2024,
                    index: 2,
                },
                {
                    x: 'Visit 3',
                    value: 34.35,
                    subjectId: 5,
                    siteId: 2024,
                    index: 3,
                },
                {
                    x: 'Visit 4',
                    value: 46.39,
                    subjectId: 5,
                    siteId: 2024,
                    index: 4,
                },
                {
                    x: 'Visit 5',
                    value: 30.96,
                    subjectId: 5,
                    siteId: 2024,
                    index: 5,
                },
                {
                    x: 'Visit 6',
                    value: 48.14,
                    subjectId: 5,
                    siteId: 2024,
                    index: 6,
                },
                {
                    x: 'Visit 7',
                    value: 55.14,
                    subjectId: 5,
                    siteId: 2024,
                    index: 7,
                },
            ],
            subjectNumber: '101-232-005',
        },
        '6': {
            visits: [
                {
                    x: 'Baseline',
                    value: 49.69,
                    subjectId: 6,
                    siteId: 2024,
                    index: 0,
                },
                {
                    x: 'Visit 1',
                    value: 30.24,
                    subjectId: 6,
                    siteId: 2024,
                    index: 1,
                },
                {
                    x: 'Visit 2',
                    value: 45.21,
                    subjectId: 6,
                    siteId: 2024,
                    index: 2,
                },
                {
                    x: 'Visit 3',
                    value: 42.95,
                    subjectId: 6,
                    siteId: 2024,
                    index: 3,
                },
                {
                    x: 'Visit 4',
                    value: 59.56,
                    subjectId: 6,
                    siteId: 2024,
                    index: 4,
                },
                {
                    x: 'Visit 5',
                    value: 59.47,
                    subjectId: 6,
                    siteId: 2024,
                    index: 5,
                },
                {
                    x: 'Visit 6',
                    value: 59.03,
                    subjectId: 6,
                    siteId: 2024,
                    index: 6,
                },
                {
                    x: 'Visit 7',
                    value: 37.91,
                    subjectId: 6,
                    siteId: 2024,
                    index: 7,
                },
                {
                    x: 'Visit 8',
                    value: 50.93,
                    subjectId: 6,
                    siteId: 2024,
                    index: 8,
                },
                {
                    x: 'Visit 9',
                    value: 56.63,
                    subjectId: 6,
                    siteId: 2024,
                    index: 9,
                },
                {
                    x: 'Visit 10',
                    value: 60.57,
                    subjectId: 6,
                    siteId: 2024,
                    index: 10,
                },
                {
                    x: 'Visit 11',
                    value: 46.23,
                    subjectId: 6,
                    siteId: 2024,
                    index: 11,
                },
                {
                    x: 'Safety FUP',
                    value: 55.65,
                    subjectId: 6,
                    siteId: 2024,
                    index: 12,
                },
            ],
            subjectNumber: '101-232-006',
        },
        '7': {
            visits: [
                {
                    x: 'Baseline',
                    value: 52.04,
                    subjectId: 7,
                    siteId: 2024,
                    index: 0,
                },
            ],
            subjectNumber: '101-232-007',
        },
    },
    lineSubjectByVisitCategories: [
        'Baseline',
        'Visit 1',
        'Visit 2',
        'Visit 3',
        'Visit 4',
        'Visit 5',
        'Visit 6',
        'Visit 7',
        'Visit 8',
        'Visit 9',
        'Visit 10',
        'Visit 11',
        'Safety FUP',
    ],
    lineEventForStudy: null,
    lineSiteForStudy: null,
    lineAverageForStudy: null,
};

export const LineSiteForStudy = [
    [
        {
            x: new Date('2019-06-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-07-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-08-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-09-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-10-31T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2019-11-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-12-31T23:59:59.999Z'),
            y: 4,
        },
        {
            x: new Date('2020-01-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-02-29T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-03-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-04-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-05-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-06-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-07-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-08-31T23:59:59.999Z'),
            y: 1,
        },
    ],
    [
        {
            x: new Date('2019-06-30T23:59:59.999Z'),
            y: 3,
        },
        {
            x: new Date('2019-07-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-08-31T23:59:59.999Z'),
            y: 4,
        },
        {
            x: new Date('2019-09-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-10-31T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2019-11-30T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2019-12-31T23:59:59.999Z'),
            y: 3,
        },
        {
            x: new Date('2020-01-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-02-29T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-03-31T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2020-04-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-05-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-06-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-07-31T23:59:59.999Z'),
            y: 3,
        },
        {
            x: new Date('2020-08-31T23:59:59.999Z'),
            y: 2,
        },
    ],
    [
        {
            x: new Date('2019-06-30T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2019-07-31T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2019-08-31T23:59:59.999Z'),
            y: 3,
        },
        {
            x: new Date('2019-09-30T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2019-10-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-11-30T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2019-12-31T23:59:59.999Z'),
            y: 4,
        },
        {
            x: new Date('2020-01-31T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2020-02-29T23:59:59.999Z'),
            y: 4,
        },
        {
            x: new Date('2020-03-31T23:59:59.999Z'),
            y: 3,
        },
        {
            x: new Date('2020-04-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-05-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-06-30T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2020-07-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-08-31T23:59:59.999Z'),
            y: 2,
        },
    ],
    [
        {
            x: new Date('2019-06-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-07-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-08-31T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2019-09-30T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2019-10-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-11-30T23:59:59.999Z'),
            y: 3,
        },
        {
            x: new Date('2019-12-31T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2020-01-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-02-29T23:59:59.999Z'),
            y: 4,
        },
        {
            x: new Date('2020-03-31T23:59:59.999Z'),
            y: 5,
        },
        {
            x: new Date('2020-04-30T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2020-05-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-06-30T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2020-07-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-08-31T23:59:59.999Z'),
            y: 2,
        },
    ],
];

export const SparkLineExample: SparkLineData[][] = [
    [
        {
            x: new Date('2019-06-30T23:59:59.999Z').toISOString(),
            y: 1,
            color: 3,
            score: 1,
        },
        {
            x: new Date('2019-07-31T23:59:59.999Z').toISOString(),
            y: 0,
            color: 1,
            score: 2,
        },
        {
            x: new Date('2019-08-31T23:59:59.999Z').toISOString(),
            y: 1,
            color: 2,
            score: 3,
        },
        {
            x: new Date('2019-09-30T23:59:59.999Z').toISOString(),
            y: 1,
            color: 1,
            score: 4,
        },
        {
            x: new Date('2019-10-31T23:59:59.999Z').toISOString(),
            y: 2,
            color: 3,
            score: 5,
        },
        {
            x: new Date('2019-11-30T23:59:59.999Z').toISOString(),
            y: 1,
            color: 1,
            score: 6,
        },
        {
            x: new Date('2019-12-31T23:59:59.999Z').toISOString(),
            y: 4,
            color: 2,
            score: 7,
        },
        {
            x: new Date('2020-01-31T23:59:59.999Z').toISOString(),
            y: 0,
            color: 3,
            score: 8,
        },
        {
            x: new Date('2020-02-29T23:59:59.999Z').toISOString(),
            y: 0,
            color: 1,
            score: 9,
        },
        {
            x: new Date('2020-03-31T23:59:59.999Z').toISOString(),
            y: 0,
            color: 2,
            score: 10,
        },
        {
            x: new Date('2020-04-30T23:59:59.999Z').toISOString(),
            y: 0,
            color: 3,
            score: 11,
        },
        {
            x: new Date('2020-05-31T23:59:59.999Z').toISOString(),
            y: 1,
            color: 1,
            score: 12,
        },
        {
            x: new Date('2020-06-30T23:59:59.999Z').toISOString(),
            y: 1,
            color: 2,
            score: 13,
        },
        {
            x: new Date('2020-07-31T23:59:59.999Z').toISOString(),
            y: 0,
            color: 3,
            score: 14,
        },
        {
            x: new Date('2020-08-31T23:59:59.999Z').toISOString(),
            y: 1,
            color: 1,
            score: 15,
        },
    ],
];

export const LineSiteForStudyCumulative = LineSiteForStudy.map((series) => {
    let cumulativeY = 0;
    return series.map((data) => {
        cumulativeY += data.y;
        return {
            x: data.x,
            y: cumulativeY,
        };
    });
});

export const LineAverageForStudy = [
    {
        x: new Date('2018-11-18T23:59:59.999Z'),
        y: 3,
    },
    {
        x: new Date('2018-11-25T23:59:59.999Z'),
        y: null,
    },
    {
        x: new Date('2018-12-02T23:59:59.999Z'),
        y: 10.954545454545453,
    },
    {
        x: new Date('2018-12-09T23:59:59.999Z'),
        y: 16.565217391304348,
    },
    {
        x: new Date('2018-12-16T23:59:59.999Z'),
        y: null,
    },
    {
        x: new Date('2018-12-23T23:59:59.999Z'),
        y: 2,
    },
    {
        x: new Date('2018-12-30T23:59:59.999Z'),
        y: 6.928571428571431,
    },
    {
        x: new Date('2019-01-06T23:59:59.999Z'),
        y: 4.120000000000001,
    },
    {
        x: new Date('2019-01-13T23:59:59.999Z'),
        y: 15.692307692307695,
    },
    {
        x: new Date('2019-01-20T23:59:59.999Z'),
        y: 19.690476190476186,
    },
    {
        x: new Date('2019-01-27T23:59:59.999Z'),
        y: 10.461538461538462,
    },
    {
        x: new Date('2019-02-03T23:59:59.999Z'),
        y: 14.222222222222225,
    },
    {
        x: new Date('2019-02-10T23:59:59.999Z'),
        y: 15.490196078431365,
    },
    {
        x: new Date('2019-02-17T23:59:59.999Z'),
        y: 17.5,
    },
    {
        x: new Date('2019-02-24T23:59:59.999Z'),
        y: 38.17333333333334,
    },
    {
        x: new Date('2019-03-03T23:59:59.999Z'),
        y: 31.750000000000004,
    },
    {
        x: new Date('2019-03-10T23:59:59.999Z'),
        y: 17.59090909090909,
    },
    {
        x: new Date('2019-03-17T23:59:59.999Z'),
        y: 28.267326732673276,
    },
    {
        x: new Date('2019-03-24T23:59:59.999Z'),
        y: 36.092783505154635,
    },
    {
        x: new Date('2019-03-31T23:59:59.999Z'),
        y: 13.481481481481483,
    },
    {
        x: new Date('2019-04-07T23:59:59.999Z'),
        y: 36.60000000000001,
    },
    {
        x: new Date('2019-04-14T23:59:59.999Z'),
        y: 9.070175438596497,
    },
    {
        x: new Date('2019-04-21T23:59:59.999Z'),
        y: 7.928571428571429,
    },
    {
        x: new Date('2019-04-28T23:59:59.999Z'),
        y: 3.88235294117647,
    },
    {
        x: new Date('2019-05-05T23:59:59.999Z'),
        y: 22.272727272727266,
    },
    {
        x: new Date('2019-05-12T23:59:59.999Z'),
        y: 15.947368421052632,
    },
    {
        x: new Date('2019-05-19T23:59:59.999Z'),
        y: 25.454545454545443,
    },
    {
        x: new Date('2019-05-26T23:59:59.999Z'),
        y: 22.382352941176467,
    },
    {
        x: new Date('2019-06-02T23:59:59.999Z'),
        y: 36.36607142857141,
    },
    {
        x: new Date('2019-06-09T23:59:59.999Z'),
        y: 11.547945205479452,
    },
    {
        x: new Date('2019-06-16T23:59:59.999Z'),
        y: 18.78787878787879,
    },
    {
        x: new Date('2019-06-23T23:59:59.999Z'),
        y: 34.73809523809524,
    },
    {
        x: new Date('2019-06-30T23:59:59.999Z'),
        y: 25.964539007092203,
    },
    {
        x: new Date('2019-07-07T23:59:59.999Z'),
        y: 9.692307692307692,
    },
    {
        x: new Date('2019-07-14T23:59:59.999Z'),
        y: 16.25000000000001,
    },
    {
        x: new Date('2019-07-21T23:59:59.999Z'),
        y: 11.008695652173914,
    },
    {
        x: new Date('2019-07-28T23:59:59.999Z'),
        y: 15.828402366863912,
    },
    {
        x: new Date('2019-08-04T23:59:59.999Z'),
        y: 21.098814229249005,
    },
    {
        x: new Date('2019-08-11T23:59:59.999Z'),
        y: 24.704663212435243,
    },
    {
        x: new Date('2019-08-18T23:59:59.999Z'),
        y: 29.36206896551724,
    },
    {
        x: new Date('2019-08-25T23:59:59.999Z'),
        y: 11.728260869565217,
    },
    {
        x: new Date('2019-09-01T23:59:59.999Z'),
        y: 19.6875,
    },
    {
        x: new Date('2019-09-08T23:59:59.999Z'),
        y: 21.466101694915242,
    },
    {
        x: new Date('2019-09-15T23:59:59.999Z'),
        y: 14.05181347150259,
    },
    {
        x: new Date('2019-09-22T23:59:59.999Z'),
        y: 49.04545454545455,
    },
    {
        x: new Date('2019-09-29T23:59:59.999Z'),
        y: 26.621118012422365,
    },
    {
        x: new Date('2019-10-06T23:59:59.999Z'),
        y: 26.53124999999999,
    },
    {
        x: new Date('2019-10-13T23:59:59.999Z'),
        y: 60.38947368421055,
    },
    {
        x: new Date('2019-10-20T23:59:59.999Z'),
        y: 25.666666666666668,
    },
    {
        x: new Date('2019-10-27T23:59:59.999Z'),
        y: 30.374149659863956,
    },
    {
        x: new Date('2019-11-03T23:59:59.999Z'),
        y: 28.195652173913057,
    },
    {
        x: new Date('2019-11-10T23:59:59.999Z'),
        y: 13.578431372549021,
    },
    {
        x: new Date('2019-11-17T23:59:59.999Z'),
        y: 22.17333333333333,
    },
    {
        x: new Date('2019-11-24T23:59:59.999Z'),
        y: 25.120805369127517,
    },
    {
        x: new Date('2019-12-01T23:59:59.999Z'),
        y: 17.24170616113744,
    },
    {
        x: new Date('2019-12-08T23:59:59.999Z'),
        y: 21.505263157894735,
    },
    {
        x: new Date('2019-12-15T23:59:59.999Z'),
        y: 30.743362831858406,
    },
    {
        x: new Date('2019-12-22T23:59:59.999Z'),
        y: 41.46511627906976,
    },
    {
        x: new Date('2019-12-29T23:59:59.999Z'),
        y: 14.476190476190473,
    },
    {
        x: new Date('2020-01-05T23:59:59.999Z'),
        y: 24.235849056603758,
    },
    {
        x: new Date('2020-01-12T23:59:59.999Z'),
        y: 54.99328859060405,
    },
    {
        x: new Date('2020-01-19T23:59:59.999Z'),
        y: 34.111731843575434,
    },
    {
        x: new Date('2020-01-26T23:59:59.999Z'),
        y: 37.14765100671143,
    },
    {
        x: new Date('2020-02-02T23:59:59.999Z'),
        y: 24.27528089887639,
    },
    {
        x: new Date('2020-02-09T23:59:59.999Z'),
        y: 43.36363636363634,
    },
    {
        x: new Date('2020-02-16T23:59:59.999Z'),
        y: 90.55803571428568,
    },
    {
        x: new Date('2020-02-23T23:59:59.999Z'),
        y: 26.33333333333334,
    },
    {
        x: new Date('2020-03-01T23:59:59.999Z'),
        y: 20.2719298245614,
    },
    {
        x: new Date('2020-03-08T23:59:59.999Z'),
        y: 25.407407407407394,
    },
    {
        x: new Date('2020-03-15T23:59:59.999Z'),
        y: 35.78191489361701,
    },
    {
        x: new Date('2020-03-22T23:59:59.999Z'),
        y: 36.30666666666668,
    },
    {
        x: new Date('2020-03-29T23:59:59.999Z'),
        y: 43.62121212121212,
    },
    {
        x: new Date('2020-04-05T23:59:59.999Z'),
        y: 39.96296296296298,
    },
    {
        x: new Date('2020-04-12T23:59:59.999Z'),
        y: 57.33582089552237,
    },
    {
        x: new Date('2020-04-19T23:59:59.999Z'),
        y: 18.558441558441555,
    },
    {
        x: new Date('2020-04-26T23:59:59.999Z'),
        y: 17.147058823529413,
    },
    {
        x: new Date('2020-05-03T23:59:59.999Z'),
        y: 57.748466257668724,
    },
    {
        x: new Date('2020-05-10T23:59:59.999Z'),
        y: 22.596153846153847,
    },
    {
        x: new Date('2020-05-17T23:59:59.999Z'),
        y: 43.333333333333336,
    },
    {
        x: new Date('2020-05-24T23:59:59.999Z'),
        y: 51.12269938650306,
    },
    {
        x: new Date('2020-05-31T23:59:59.999Z'),
        y: 26.74418604651163,
    },
    {
        x: new Date('2020-06-07T23:59:59.999Z'),
        y: 7.7520661157024815,
    },
    {
        x: new Date('2020-06-14T23:59:59.999Z'),
        y: 27.075757575757585,
    },
    {
        x: new Date('2020-06-21T23:59:59.999Z'),
        y: 27.49677419354842,
    },
    {
        x: new Date('2020-06-28T23:59:59.999Z'),
        y: 17.900584795321627,
    },
    {
        x: new Date('2020-07-05T23:59:59.999Z'),
        y: 12.946478873239434,
    },
    {
        x: new Date('2020-07-12T23:59:59.999Z'),
        y: 19.67588932806325,
    },
    {
        x: new Date('2020-07-19T23:59:59.999Z'),
        y: 38.68846153846158,
    },
    {
        x: new Date('2020-07-26T23:59:59.999Z'),
        y: 26.15811965811965,
    },
    {
        x: new Date('2020-08-02T23:59:59.999Z'),
        y: 42.901960784313694,
    },
    {
        x: new Date('2020-08-09T23:59:59.999Z'),
        y: 23.370370370370377,
    },
    {
        x: new Date('2020-08-16T23:59:59.999Z'),
        y: 48.85444743935308,
    },
    {
        x: new Date('2020-08-23T23:59:59.999Z'),
        y: 13.401639344262291,
    },
    {
        x: new Date('2020-08-30T23:59:59.999Z'),
        y: 48.10471204188482,
    },
    {
        x: new Date('2020-09-06T23:59:59.999Z'),
        y: 20.746666666666673,
    },
    {
        x: new Date('2020-09-13T23:59:59.999Z'),
        y: 13.050505050505054,
    },
    {
        x: new Date('2020-09-20T23:59:59.999Z'),
        y: 39.655430711610485,
    },
    {
        x: new Date('2020-09-27T23:59:59.999Z'),
        y: 40.94652406417114,
    },
    {
        x: new Date('2020-10-04T23:59:59.999Z'),
        y: 32.88907849829353,
    },
    {
        x: new Date('2020-10-11T23:59:59.999Z'),
        y: 7.199999999999998,
    },
];

export const LineAverageForStudySpecific = [
    {
        x: new Date('2018-11-18T23:59:59.999Z'),
        y: 3,
    },
    {
        x: new Date('2018-11-25T23:59:59.999Z'),
        y: null,
    },
    {
        x: new Date('2018-12-02T23:59:59.999Z'),
        y: 11,
    },
    {
        x: new Date('2018-12-09T23:59:59.999Z'),
        y: 16,
    },
    {
        x: new Date('2018-12-16T23:59:59.999Z'),
        y: null,
    },
    {
        x: new Date('2018-12-23T23:59:59.999Z'),
        y: 2,
    },
    {
        x: new Date('2018-12-30T23:59:59.999Z'),
        y: 4.5,
    },
    {
        x: new Date('2019-01-06T23:59:59.999Z'),
        y: 4,
    },
    {
        x: new Date('2019-01-13T23:59:59.999Z'),
        y: 21,
    },
    {
        x: new Date('2019-01-20T23:59:59.999Z'),
        y: 26.5,
    },
    {
        x: new Date('2019-01-27T23:59:59.999Z'),
        y: 12,
    },
    {
        x: new Date('2019-02-03T23:59:59.999Z'),
        y: 14,
    },
    {
        x: new Date('2019-02-10T23:59:59.999Z'),
        y: 16,
    },
    {
        x: new Date('2019-02-17T23:59:59.999Z'),
        y: 17.5,
    },
    {
        x: new Date('2019-02-24T23:59:59.999Z'),
        y: 34,
    },
    {
        x: new Date('2019-03-03T23:59:59.999Z'),
        y: 32,
    },
    {
        x: new Date('2019-03-10T23:59:59.999Z'),
        y: 17,
    },
    {
        x: new Date('2019-03-17T23:59:59.999Z'),
        y: 18,
    },
    {
        x: new Date('2019-03-24T23:59:59.999Z'),
        y: 46,
    },
    {
        x: new Date('2019-03-31T23:59:59.999Z'),
        y: 16,
    },
    {
        x: new Date('2019-04-07T23:59:59.999Z'),
        y: 42,
    },
    {
        x: new Date('2019-04-14T23:59:59.999Z'),
        y: 9,
    },
    {
        x: new Date('2019-04-21T23:59:59.999Z'),
        y: 4,
    },
    {
        x: new Date('2019-04-28T23:59:59.999Z'),
        y: 3,
    },
    {
        x: new Date('2019-05-05T23:59:59.999Z'),
        y: 8,
    },
    {
        x: new Date('2019-05-12T23:59:59.999Z'),
        y: 9,
    },
    {
        x: new Date('2019-05-19T23:59:59.999Z'),
        y: 19,
    },
    {
        x: new Date('2019-05-26T23:59:59.999Z'),
        y: 7,
    },
    {
        x: new Date('2019-06-02T23:59:59.999Z'),
        y: 45,
    },
    {
        x: new Date('2019-06-09T23:59:59.999Z'),
        y: 10,
    },
    {
        x: new Date('2019-06-16T23:59:59.999Z'),
        y: 12,
    },
    {
        x: new Date('2019-06-23T23:59:59.999Z'),
        y: 36.5,
    },
    {
        x: new Date('2019-06-30T23:59:59.999Z'),
        y: 20,
    },
    {
        x: new Date('2019-07-07T23:59:59.999Z'),
        y: 10,
    },
    {
        x: new Date('2019-07-14T23:59:59.999Z'),
        y: 11,
    },
    {
        x: new Date('2019-07-21T23:59:59.999Z'),
        y: 5,
    },
    {
        x: new Date('2019-07-28T23:59:59.999Z'),
        y: 11,
    },
    {
        x: new Date('2019-08-04T23:59:59.999Z'),
        y: 17,
    },
    {
        x: new Date('2019-08-11T23:59:59.999Z'),
        y: 20,
    },
    {
        x: new Date('2019-08-18T23:59:59.999Z'),
        y: 26,
    },
    {
        x: new Date('2019-08-25T23:59:59.999Z'),
        y: 9,
    },
    {
        x: new Date('2019-09-01T23:59:59.999Z'),
        y: 17.5,
    },
    {
        x: new Date('2019-09-08T23:59:59.999Z'),
        y: 15.5,
    },
    {
        x: new Date('2019-09-15T23:59:59.999Z'),
        y: 12,
    },
    {
        x: new Date('2019-09-22T23:59:59.999Z'),
        y: 33,
    },
    {
        x: new Date('2019-09-29T23:59:59.999Z'),
        y: 16,
    },
    {
        x: new Date('2019-10-06T23:59:59.999Z'),
        y: 18,
    },
    {
        x: new Date('2019-10-13T23:59:59.999Z'),
        y: 59,
    },
    {
        x: new Date('2019-10-20T23:59:59.999Z'),
        y: 19,
    },
    {
        x: new Date('2019-10-27T23:59:59.999Z'),
        y: 29,
    },
    {
        x: new Date('2019-11-03T23:59:59.999Z'),
        y: 18,
    },
    {
        x: new Date('2019-11-10T23:59:59.999Z'),
        y: 9.5,
    },
    {
        x: new Date('2019-11-17T23:59:59.999Z'),
        y: 8,
    },
    {
        x: new Date('2019-11-24T23:59:59.999Z'),
        y: 9,
    },
    {
        x: new Date('2019-12-01T23:59:59.999Z'),
        y: 8,
    },
    {
        x: new Date('2019-12-08T23:59:59.999Z'),
        y: 20,
    },
    {
        x: new Date('2019-12-15T23:59:59.999Z'),
        y: 17,
    },
    {
        x: new Date('2019-12-22T23:59:59.999Z'),
        y: 23,
    },
    {
        x: new Date('2019-12-29T23:59:59.999Z'),
        y: 9,
    },
    {
        x: new Date('2020-01-05T23:59:59.999Z'),
        y: 26,
    },
    {
        x: new Date('2020-01-12T23:59:59.999Z'),
        y: 44,
    },
    {
        x: new Date('2020-01-19T23:59:59.999Z'),
        y: 31,
    },
    {
        x: new Date('2020-01-26T23:59:59.999Z'),
        y: 36,
    },
    {
        x: new Date('2020-02-02T23:59:59.999Z'),
        y: 14.5,
    },
    {
        x: new Date('2020-02-09T23:59:59.999Z'),
        y: 13,
    },
    {
        x: new Date('2020-02-16T23:59:59.999Z'),
        y: 95,
    },
    {
        x: new Date('2020-02-23T23:59:59.999Z'),
        y: 13,
    },
    {
        x: new Date('2020-03-01T23:59:59.999Z'),
        y: 9,
    },
    {
        x: new Date('2020-03-08T23:59:59.999Z'),
        y: 11,
    },
    {
        x: new Date('2020-03-15T23:59:59.999Z'),
        y: 14,
    },
    {
        x: new Date('2020-03-22T23:59:59.999Z'),
        y: 36,
    },
    {
        x: new Date('2020-03-29T23:59:59.999Z'),
        y: 34,
    },
    {
        x: new Date('2020-04-05T23:59:59.999Z'),
        y: 21,
    },
    {
        x: new Date('2020-04-12T23:59:59.999Z'),
        y: 45,
    },
    {
        x: new Date('2020-04-19T23:59:59.999Z'),
        y: 17,
    },
    {
        x: new Date('2020-04-26T23:59:59.999Z'),
        y: 7.5,
    },
    {
        x: new Date('2020-05-03T23:59:59.999Z'),
        y: 51,
    },
    {
        x: new Date('2020-05-10T23:59:59.999Z'),
        y: 13,
    },
    {
        x: new Date('2020-05-17T23:59:59.999Z'),
        y: 14,
    },
    {
        x: new Date('2020-05-24T23:59:59.999Z'),
        y: 52,
    },
    {
        x: new Date('2020-05-31T23:59:59.999Z'),
        y: 8,
    },
    {
        x: new Date('2020-06-07T23:59:59.999Z'),
        y: 6,
    },
    {
        x: new Date('2020-06-14T23:59:59.999Z'),
        y: 15,
    },
    {
        x: new Date('2020-06-21T23:59:59.999Z'),
        y: 16,
    },
    {
        x: new Date('2020-06-28T23:59:59.999Z'),
        y: 21,
    },
    {
        x: new Date('2020-07-05T23:59:59.999Z'),
        y: 8,
    },
    {
        x: new Date('2020-07-12T23:59:59.999Z'),
        y: 13,
    },
    {
        x: new Date('2020-07-19T23:59:59.999Z'),
        y: 22,
    },
    {
        x: new Date('2020-07-26T23:59:59.999Z'),
        y: 11,
    },
    {
        x: new Date('2020-08-02T23:59:59.999Z'),
        y: 17,
    },
    {
        x: new Date('2020-08-09T23:59:59.999Z'),
        y: 7,
    },
    {
        x: new Date('2020-08-16T23:59:59.999Z'),
        y: 13,
    },
    {
        x: new Date('2020-08-23T23:59:59.999Z'),
        y: 8,
    },
    {
        x: new Date('2020-08-30T23:59:59.999Z'),
        y: 8,
    },
    {
        x: new Date('2020-09-06T23:59:59.999Z'),
        y: 16,
    },
    {
        x: new Date('2020-09-13T23:59:59.999Z'),
        y: 8,
    },
    {
        x: new Date('2020-09-20T23:59:59.999Z'),
        y: 18,
    },
    {
        x: new Date('2020-09-27T23:59:59.999Z'),
        y: 23,
    },
    {
        x: new Date('2020-10-04T23:59:59.999Z'),
        y: 26,
    },
    {
        x: new Date('2020-10-11T23:59:59.999Z'),
        y: 4.5,
    },
];

export const LineEventForStudy: Record<string, { x: Date; y: number | null }[]> = {
    'RECOVERED/RESOLVED WITH SEQUELAE': [
        {
            x: new Date('2018-11-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2018-12-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-01-31T23:59:59.999Z'),
            y: 10,
        },
        {
            x: new Date('2019-02-28T23:59:59.999Z'),
            y: 6,
        },
        {
            x: new Date('2019-03-31T23:59:59.999Z'),
            y: 5,
        },
        {
            x: new Date('2019-04-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-05-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-06-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-07-31T23:59:59.999Z'),
            y: 17,
        },
        {
            x: new Date('2019-08-31T23:59:59.999Z'),
            y: 20,
        },
        {
            x: new Date('2019-09-30T23:59:59.999Z'),
            y: 5,
        },
        {
            x: new Date('2019-10-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-11-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-12-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-01-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-02-29T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2020-03-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-04-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-05-31T23:59:59.999Z'),
            y: 7,
        },
        {
            x: new Date('2020-06-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-07-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-08-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-09-30T23:59:59.999Z'),
            y: 0,
        },
    ],
    'RECOVERED/RESOLVED': [
        {
            x: new Date('2018-11-30T23:59:59.999Z'),
            y: 5,
        },
        {
            x: new Date('2018-12-31T23:59:59.999Z'),
            y: 32,
        },
        {
            x: new Date('2019-01-31T23:59:59.999Z'),
            y: 43,
        },
        {
            x: new Date('2019-02-28T23:59:59.999Z'),
            y: 68,
        },
        {
            x: new Date('2019-03-31T23:59:59.999Z'),
            y: 44,
        },
        {
            x: new Date('2019-04-30T23:59:59.999Z'),
            y: 56,
        },
        {
            x: new Date('2019-05-31T23:59:59.999Z'),
            y: 50,
        },
        {
            x: new Date('2019-06-30T23:59:59.999Z'),
            y: 67,
        },
        {
            x: new Date('2019-07-31T23:59:59.999Z'),
            y: 138,
        },
        {
            x: new Date('2019-08-31T23:59:59.999Z'),
            y: 175,
        },
        {
            x: new Date('2019-09-30T23:59:59.999Z'),
            y: 113,
        },
        {
            x: new Date('2019-10-31T23:59:59.999Z'),
            y: 80,
        },
        {
            x: new Date('2019-11-30T23:59:59.999Z'),
            y: 132,
        },
        {
            x: new Date('2019-12-31T23:59:59.999Z'),
            y: 127,
        },
        {
            x: new Date('2020-01-31T23:59:59.999Z'),
            y: 114,
        },
        {
            x: new Date('2020-02-29T23:59:59.999Z'),
            y: 74,
        },
        {
            x: new Date('2020-03-31T23:59:59.999Z'),
            y: 118,
        },
        {
            x: new Date('2020-04-30T23:59:59.999Z'),
            y: 41,
        },
        {
            x: new Date('2020-05-31T23:59:59.999Z'),
            y: 22,
        },
        {
            x: new Date('2020-06-30T23:59:59.999Z'),
            y: 96,
        },
        {
            x: new Date('2020-07-31T23:59:59.999Z'),
            y: 120,
        },
        {
            x: new Date('2020-08-31T23:59:59.999Z'),
            y: 38,
        },
        {
            x: new Date('2020-09-30T23:59:59.999Z'),
            y: 1,
        },
    ],
    'NOT RECOVERED/NOT RESOLVED': [
        {
            x: new Date('2018-11-30T23:59:59.999Z'),
            y: 4,
        },
        {
            x: new Date('2018-12-31T23:59:59.999Z'),
            y: 10,
        },
        {
            x: new Date('2019-01-31T23:59:59.999Z'),
            y: 28,
        },
        {
            x: new Date('2019-02-28T23:59:59.999Z'),
            y: 35,
        },
        {
            x: new Date('2019-03-31T23:59:59.999Z'),
            y: 19,
        },
        {
            x: new Date('2019-04-30T23:59:59.999Z'),
            y: 11,
        },
        {
            x: new Date('2019-05-31T23:59:59.999Z'),
            y: 29,
        },
        {
            x: new Date('2019-06-30T23:59:59.999Z'),
            y: 17,
        },
        {
            x: new Date('2019-07-31T23:59:59.999Z'),
            y: 43,
        },
        {
            x: new Date('2019-08-31T23:59:59.999Z'),
            y: 45,
        },
        {
            x: new Date('2019-09-30T23:59:59.999Z'),
            y: 60,
        },
        {
            x: new Date('2019-10-31T23:59:59.999Z'),
            y: 31,
        },
        {
            x: new Date('2019-11-30T23:59:59.999Z'),
            y: 43,
        },
        {
            x: new Date('2019-12-31T23:59:59.999Z'),
            y: 32,
        },
        {
            x: new Date('2020-01-31T23:59:59.999Z'),
            y: 56,
        },
        {
            x: new Date('2020-02-29T23:59:59.999Z'),
            y: 42,
        },
        {
            x: new Date('2020-03-31T23:59:59.999Z'),
            y: 46,
        },
        {
            x: new Date('2020-04-30T23:59:59.999Z'),
            y: 20,
        },
        {
            x: new Date('2020-05-31T23:59:59.999Z'),
            y: 26,
        },
        {
            x: new Date('2020-06-30T23:59:59.999Z'),
            y: 49,
        },
        {
            x: new Date('2020-07-31T23:59:59.999Z'),
            y: 39,
        },
        {
            x: new Date('2020-08-31T23:59:59.999Z'),
            y: 35,
        },
        {
            x: new Date('2020-09-30T23:59:59.999Z'),
            y: 3,
        },
    ],
    FATAL: [
        {
            x: new Date('2018-11-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2018-12-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-01-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-02-28T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-03-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-04-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-05-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-06-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-07-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-08-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-09-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2019-10-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-11-30T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2019-12-31T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-01-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-02-29T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-03-31T23:59:59.999Z'),
            y: 2,
        },
        {
            x: new Date('2020-04-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-05-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-06-30T23:59:59.999Z'),
            y: 0,
        },
        {
            x: new Date('2020-07-31T23:59:59.999Z'),
            y: 1,
        },
        {
            x: new Date('2020-08-31T23:59:59.999Z'),
            y: 3,
        },
        {
            x: new Date('2020-09-30T23:59:59.999Z'),
            y: 1,
        },
    ],
};

export const LineSubjectForSubject: Record<number, { x: Date; y: number | null }[]> = {
    45395438: [
        {
            x: new Date('2020-05-20T00:00:00Z'),
            y: 1,
        },
        {
            x: new Date('2020-05-26T00:00:00Z'),
            y: 2,
        },
        {
            x: new Date('2020-05-27T00:00:00Z'),
            y: 3,
        },
        {
            x: new Date('2020-05-28T00:00:00Z'),
            y: 4,
        },
        {
            x: new Date('2020-05-29T00:00:00Z'),
            y: 5,
        },
        {
            x: new Date('2020-05-30T00:00:00Z'),
            y: 6,
        },
        {
            x: new Date('2020-05-31T00:00:00Z'),
            y: 7,
        },
        {
            x: new Date('2020-06-01T00:00:00Z'),
            y: 8,
        },
        {
            x: new Date('2020-06-02T00:00:00Z'),
            y: 9,
        },
        {
            x: new Date('2020-06-03T00:00:00Z'),
            y: 10,
        },
        {
            x: new Date('2020-07-26T00:00:00Z'),
            y: 11,
        },
    ],
    83314116: [
        {
            x: new Date('2019-07-18T00:00:00Z'),
            y: 1,
        },
        {
            x: new Date('2019-07-23T00:00:00Z'),
            y: 2,
        },
        {
            x: new Date('2019-07-24T00:00:00Z'),
            y: 3,
        },
        {
            x: new Date('2019-07-25T00:00:00Z'),
            y: 4,
        },
        {
            x: new Date('2019-07-26T00:00:00Z'),
            y: 5,
        },
        {
            x: new Date('2019-07-27T00:00:00Z'),
            y: 6,
        },
        {
            x: new Date('2019-07-28T00:00:00Z'),
            y: 7,
        },
        {
            x: new Date('2019-07-29T00:00:00Z'),
            y: 8,
        },
        {
            x: new Date('2019-07-30T00:00:00Z'),
            y: 9,
        },
        {
            x: new Date('2019-07-31T00:00:00Z'),
            y: 10,
        },
        {
            x: new Date('2019-09-22T00:00:00Z'),
            y: 11,
        },
        {
            x: new Date('2019-09-23T00:00:00Z'),
            y: 13,
        },
        {
            x: new Date('2019-09-24T00:00:00Z'),
            y: 14,
        },
        {
            x: new Date('2019-09-25T00:00:00Z'),
            y: 15,
        },
        {
            x: new Date('2019-09-26T00:00:00Z'),
            y: 16,
        },
        {
            x: new Date('2019-09-27T00:00:00Z'),
            y: 17,
        },
        {
            x: new Date('2019-09-28T00:00:00Z'),
            y: 18,
        },
        {
            x: new Date('2019-09-29T00:00:00Z'),
            y: 19,
        },
        {
            x: new Date('2019-09-30T00:00:00Z'),
            y: 20,
        },
        {
            x: new Date('2019-11-04T00:00:00Z'),
            y: 21,
        },
        {
            x: new Date('2019-12-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-01-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-02-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-03-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-04-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-05-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-06-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-07-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-08-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-09-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-10-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-11-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-12-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-01-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-02-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-03-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-04-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-05-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-06-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-07-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-08-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-09-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-10-04T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-11-04T00:00:00Z'),
            y: 34,
        },
    ],
    97509398: [
        {
            x: new Date('2020-09-17T00:00:00Z'),
            y: 1,
        },
    ],
    108561782: [
        {
            x: new Date('2019-07-15T00:00:00Z'),
            y: 1,
        },
        {
            x: new Date('2019-07-19T00:00:00Z'),
            y: 2,
        },
        {
            x: new Date('2019-07-20T00:00:00Z'),
            y: 3,
        },
        {
            x: new Date('2019-07-21T00:00:00Z'),
            y: 4,
        },
        {
            x: new Date('2019-07-22T00:00:00Z'),
            y: 5,
        },
        {
            x: new Date('2019-07-23T00:00:00Z'),
            y: 6,
        },
        {
            x: new Date('2019-07-24T00:00:00Z'),
            y: 7,
        },
        {
            x: new Date('2019-07-25T00:00:00Z'),
            y: 8,
        },
        {
            x: new Date('2019-07-26T00:00:00Z'),
            y: 9,
        },
        {
            x: new Date('2019-07-27T00:00:00Z'),
            y: 10,
        },
        {
            x: new Date('2019-08-30T00:00:00Z'),
            y: 12,
        },
        {
            x: new Date('2019-08-31T00:00:00Z'),
            y: 13,
        },
        {
            x: new Date('2019-09-01T00:00:00Z'),
            y: 14,
        },
        {
            x: new Date('2019-09-02T00:00:00Z'),
            y: 15,
        },
        {
            x: new Date('2019-09-03T00:00:00Z'),
            y: 16,
        },
        {
            x: new Date('2019-09-04T00:00:00Z'),
            y: 17,
        },
        {
            x: new Date('2019-09-05T00:00:00Z'),
            y: 18,
        },
        {
            x: new Date('2019-09-06T00:00:00Z'),
            y: 19,
        },
        {
            x: new Date('2019-09-07T00:00:00Z'),
            y: 20,
        },
        {
            x: new Date('2019-09-18T00:00:00Z'),
            y: 11,
        },
        {
            x: new Date('2019-10-10T00:00:00Z'),
            y: 22,
        },
        {
            x: new Date('2019-10-11T00:00:00Z'),
            y: 23,
        },
        {
            x: new Date('2019-10-12T00:00:00Z'),
            y: 21,
        },
        {
            x: new Date('2019-10-13T00:00:00Z'),
            y: 25,
        },
        {
            x: new Date('2019-10-14T00:00:00Z'),
            y: 26,
        },
        {
            x: new Date('2019-10-15T00:00:00Z'),
            y: 27,
        },
        {
            x: new Date('2019-10-16T00:00:00Z'),
            y: 28,
        },
        {
            x: new Date('2019-10-17T00:00:00Z'),
            y: 29,
        },
        {
            x: new Date('2019-10-18T00:00:00Z'),
            y: 30,
        },
        {
            x: new Date('2019-11-13T00:00:00Z'),
            y: 32,
        },
        {
            x: new Date('2019-11-22T00:00:00Z'),
            y: 31,
        },
        {
            x: new Date('2019-12-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-01-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-02-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-03-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-04-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-05-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-06-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-07-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-08-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-09-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-10-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-11-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2020-12-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-01-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-02-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-03-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-04-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-05-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-06-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-07-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-08-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-09-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-10-13T00:00:00Z'),
            y: 34,
        },
        {
            x: new Date('2021-11-13T00:00:00Z'),
            y: 34,
        },
    ],
    129174149: [
        {
            x: new Date('2019-08-13T00:00:00Z'),
            y: 1,
        },
        {
            x: new Date('2019-08-20T00:00:00Z'),
            y: 2,
        },
        {
            x: new Date('2019-08-21T00:00:00Z'),
            y: 3,
        },
        {
            x: new Date('2019-08-22T00:00:00Z'),
            y: 4,
        },
        {
            x: new Date('2019-08-23T00:00:00Z'),
            y: 5,
        },
        {
            x: new Date('2019-08-24T00:00:00Z'),
            y: 6,
        },
        {
            x: new Date('2019-08-25T00:00:00Z'),
            y: 7,
        },
        {
            x: new Date('2019-08-26T00:00:00Z'),
            y: 8,
        },
        {
            x: new Date('2019-08-27T00:00:00Z'),
            y: 9,
        },
        {
            x: new Date('2019-08-28T00:00:00Z'),
            y: 10,
        },
        {
            x: new Date('2019-10-20T00:00:00Z'),
            y: 11,
        },
    ],
};
